import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { Template } from "devextreme-react/core/template";
import { useTheme } from "@monolith-forensics/monolith-ui";
import { StorageAPI } from "@/Features/Storage/storage.api";
import { LocationsAPI } from "@/Features/Locations/locations.api";
import { usePermissions } from "@/hooks/usePermissions";
import { getIpAddress } from "@/api";
import {
  db_timestamp,
  getDateFormat,
  validateTimestamp,
} from "@/utils/date-format";
import UserApi from "@/api/users/users.js";
import UserSelectBoxItem from "@/components/SelectBoxItems/UserSelectBoxItem";
import { SignatureField } from "@/Features";
import ToolBarItems from "@/components/ToolBarItems.js";
import Loader from "@/components/Loader.js";

const destroyedLocationId = LocationsAPI.metaLocations.destroyed.location_id;

// Rows a bulk destroy can act on. The API applies the same filtering
// server-side; this keeps the client-side count and payload honest.
// is_released is the server's own flag (`storage.location_id = 1`), so this
// mirrors the server exactly — including the ENG-1403 collision, where an
// item stored at a real location with id 1 is skipped as released.
const isDestroyable = (r) =>
  !r.is_released && r.location_id !== destroyedLocationId;

export const DestroyItemsPopup = ({
  caseInfo,
  onSubmit = () => {},
  open,
  handleClose = () => {},
  table,
  query,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const destroyedBySig = useRef(null);
  // Default timestamp for the form, refreshed each time the modal opens.
  const openedAt = useMemo(() => (open ? new Date() : null), [open]);
  // Ref guards synchronously against a double click; state drives the
  // button's loading UI (a ref change alone would never re-render).
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: users } = useQuery({
    enabled: open,
    queryKey: [
      "users",
      "list",
      { includeObservers: false, includeInactive: false },
    ],
    queryFn: () =>
      UserApi.getUsers({ includeObservers: false, includeInactive: false }),
  });

  const selectionState = table?.getSelectionState() || null;

  // For an explicit selection the destroyable count is knowable client-side;
  // for select-all / exclusion selections the server resolves the final set,
  // so the raw selection total is the best available estimate.
  const total =
    selectionState === "someIncluded"
      ? table?.getSelectedRows().filter(isDestroyable).length || 0
      : table?.getVirtualSelectionTotal() || 0;

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      const data = { ...form.current.instance.option().formData };

      // Read the signature out of the ref before anything can re-render this
      // popup. setIsSubmitting below re-renders, and an unmemoized DevExtreme
      // Form rebuilds its items on re-render, which remounts the
      // SignatureField and reassigns sigFieldRef.current with empty state.
      // Reading the ref after that point always reported an empty signature
      // and sent custody_from_sig: null (ENG-1793). The Form is memoized now,
      // but the payload is built from this snapshot either way.
      const sigIsEmpty = destroyedBySig.current.isEmpty();
      const sigIsESignature = destroyedBySig.current.isESignature;
      const sigData = sigIsEmpty ? null : destroyedBySig.current.getSignature();

      // An e-signature applies the logged-in user's saved signature, so it
      // must not be attributed to anyone else; a hand-drawn signature for
      // another user is fine (they sign on screen).
      // isEmpty() is checked because SignatureField's Clear button leaves
      // isESignature set; a cleared signature has nothing to attribute.
      if (
        sigIsESignature &&
        !sigIsEmpty &&
        data.destroyed_by.user_id !== currentUser.user_id
      ) {
        enqueueSnackbar(
          "An e-signature can only be applied when you are the Destroyed By user. Clear it and have the selected user sign manually.",
          { variant: "error" }
        );
        return;
      }

      if (total === 0) {
        const selectedCount = table?.getVirtualSelectionTotal() || 0;
        // Nothing in this selection can be destroyed, so leaving it set would
        // dead-end the next bulk action — and re-opening Destroy would just
        // close again. Matches the terminal-close path below.
        table?.clearSelections();
        enqueueSnackbar(
          selectedCount > 0
            ? "None of the selected storage items can be destroyed — they are already released or destroyed."
            : "No storage items selected.",
          { variant: "warning" }
        );
        handleClose();
        return;
      }

      if (submitting.current) return;
      submitting.current = true;
      setIsSubmitting(true);

      try {
        const moveInfo = {
          caseInfo: {
            case_id: caseInfo.case_id,
            case_number: caseInfo.case_number,
            case_ref: caseInfo.case_ref,
          },
          options: {
            selectionState,
            included:
              table
                ?.getSelectedRows()
                // Ignore items that are already released or destroyed
                .filter(isDestroyable)
                .map((r) => r.uuid) || [],
            excluded: table?.getExcludedRows().map((r) => r.uuid) || [],
            ...query,
          },
        };

        const ipAddress = await getIpAddress();

        const signature = {};

        signature.custody_from_sig = sigIsEmpty
          ? null
          : {
              signature: sigData,
              signer: data.destroyed_by.full_name,
              timestamp: moment().toISOString(),
              userAgent: window.navigator.userAgent,
              ip_address: ipAddress,
              monolith_user: {
                first_name: currentUser.first_name,
                last_name: currentUser.last_name,
                full_name: currentUser.full_name,
                email: currentUser.email,
                title: currentUser.title,
                user_id: currentUser.user_id,
              },
              esig: sigIsESignature,
            };

        const formData = {
          location_name: data.destroyed_by.full_name,
          // The API overrides these with the Destroyed meta location for
          // coc_type "Destroy"; they are still sent so API versions without
          // that handling don't write a NULL location.
          new_location_id: destroyedLocationId,
          new_location_name: LocationsAPI.metaLocations.destroyed.location_name,
          timestamp: db_timestamp(data.timestamp, true),
          reason: data.reason || "Storage Item Destroyed",
          entered_by: currentUser.full_name,
          entered_by_id: currentUser.user_id,
          coc_type: "Destroy",
          signature: JSON.stringify(signature),
        };

        const result = await StorageAPI.moveStorageItems({
          formData,
          options: moveInfo.options,
          caseInfo: moveInfo.caseInfo,
        });

        if (!result?.success) {
          // The API can fail after Destroy COC rows were committed (location
          // update failed); refetch so the table reflects any committed
          // state instead of showing pre-destroy data.
          onSubmit();

          const skippedCount = result?.skipped_count || 0;
          const notFoundCount = (result?.not_found_uuids || []).length;

          // Retryable: httpPatch resolves transport and HTTP errors to
          // { success: false, error } instead of rejecting (see handleError in
          // src/api/connection.js), and the API reports work that committed
          // and then failed in failed_uuids. Keep the modal, the form and the
          // selection so the Destroyed By user, the timestamp, the notes and a
          // drawn signature survive into the retry.
          if (result?.error || (result?.failed_uuids || []).length > 0) {
            enqueueSnackbar(
              result?.message || "Failed to destroy storage items.",
              {
                variant: "error",
              }
            );
            return;
          }

          // Everything below is the server rejecting this selection
          // deterministically, so re-submitting cannot change the outcome.
          // Holding the modal open on those made Destroy look like a dead
          // button (ENG-1793) — close instead.
          if (result?.destroyed_count === 0) {
            const parts = [];
            if (skippedCount > 0)
              parts.push(`${skippedCount} already released or destroyed`);
            if (notFoundCount > 0)
              parts.push(
                `${notFoundCount} not found (deleted since selection)`
              );

            // The server accounts for every item it resolved. getStorage caps
            // a resolved selection at 1000 rows, so a larger select-all comes
            // back accounting for only part of it; and rows deleted between
            // selection and submit go unaccounted for on query-backed
            // selections, which report no not_found_uuids. That shortfall is
            // real work that never happened, so it must not be reported as
            // "nothing to destroy" right after the short-count path told the
            // user to submit again for the remainder — name it instead.
            const unaccounted = total - skippedCount - notFoundCount;

            // The selection is dropped either way. Re-submitting it cannot
            // make progress: the server re-resolves the same capped page, and
            // rows that went missing are gone. Leaving it set would only arm
            // the next bulk action with rows this run already destroyed — a
            // bulk Move filters on is_released, which destroyed items are not.
            table?.clearSelections();

            // No "submit again" instruction: it only holds for rows deleted
            // since selection. On the row-cap path reselecting resolves the
            // same capped page, and the client cannot tell the two apart.
            enqueueSnackbar(
              unaccounted > 0
                ? `No storage items were destroyed${
                    parts.length > 0 ? `: ${parts.join(", ")}` : ""
                  }. ${unaccounted} selected item(s) were not processed; ` +
                    "the table has been refreshed."
                : `No storage items were destroyed: ${parts.join(", ")}.`,
              { variant: "warning" }
            );

            handleClose();
            return;
          }

          // No destroy accounting at all: the request was refused before the
          // selection was resolved (missing values, an invalid selection
          // state, or an api image predating bulk Destroy). Deterministic, so
          // a retry is pointless — report it and close.
          enqueueSnackbar(
            result?.message || "Failed to destroy storage items.",
            {
              variant: "error",
            }
          );
          handleClose();
          return;
        }

        // success: true can still carry partial failures (COC inserts that
        // failed server-side), skipped ineligible items, and uuids deleted
        // since selection — surface each rather than reporting an
        // unqualified success.
        const {
          destroyed_count,
          skipped_count = 0,
          failed_uuids = [],
          not_found_uuids = [],
        } = result;

        // failed_uuids are the only identifiers for items that were not
        // destroyed. Keep the modal open and the selection intact so the
        // user can retry; a retained selection converges on retry — items
        // whose Destroy row committed rejoin via the API's stranded-item
        // handling. Clearing here would discard the failed set for good,
        // unrecoverably for query-backed selections.
        if (failed_uuids.length > 0) {
          onSubmit();
          enqueueSnackbar(
            `${destroyed_count} destroyed, ${failed_uuids.length} failed. ` +
              "The failed items are still selected — submit again to retry.",
            { variant: "error" }
          );
          return;
        }

        // The server resolves select-all / exclusion selections through a
        // paged list capped at 1000 rows, so a large selection can come back
        // as a clean success that only covered part of the set. Keep the
        // selection and the modal so the remainder can be submitted again
        // (rows already destroyed are skipped server-side, so the retry
        // converges). A row deleted between selection and submit can trip
        // this once; the retry then reports a clean run.
        const processed =
          (destroyed_count ?? 0) + skipped_count + not_found_uuids.length;
        if (
          destroyed_count != null &&
          selectionState !== "someIncluded" &&
          processed < total
        ) {
          onSubmit();
          enqueueSnackbar(
            `${destroyed_count} destroyed, ${
              total - processed
            } not yet processed. ` +
              "The selection is retained — submit again for the remainder.",
            { variant: "warning" }
          );
          return;
        }

        // Selection snapshots keep their pre-destroy location_id; left
        // selected, they would dead-end the next bulk action.
        table?.clearSelections();

        handleClose();

        onSubmit();

        if (destroyed_count == null) {
          // On-prem installs run the app and api as separately versioned
          // Docker images, so a success from an api image predating the
          // count fields is a real deployment state, not dead code.
          enqueueSnackbar("Storage Items Destroyed", { variant: "success" });
        } else if (skipped_count > 0 || not_found_uuids.length > 0) {
          const parts = [`${destroyed_count} destroyed`];
          if (skipped_count > 0)
            parts.push(
              `${skipped_count} skipped (already released or destroyed)`
            );
          if (not_found_uuids.length > 0)
            parts.push(
              `${not_found_uuids.length} not found (deleted since selection)`
            );
          enqueueSnackbar(`Storage items: ${parts.join(", ")}.`, {
            variant: "warning",
          });
        } else {
          enqueueSnackbar(
            `${destroyed_count} storage item${
              destroyed_count === 1 ? "" : "s"
            } destroyed.`,
            { variant: "success" }
          );
        }
      } catch {
        // Nothing HTTP reaches here: httpPatch resolves transport and HTTP
        // errors to { success: false, error } (see the retryable branch
        // above), so this only catches a throw while building the payload or
        // handling the result. Refetch anyway — a throw after the request
        // resolved means Destroy rows may have committed, and the table must
        // not keep showing pre-destroy rows. The modal and selection stay for
        // a retry, and the toast keeps this from ending in a stopped spinner.
        onSubmit();
        enqueueSnackbar("Failed to destroy storage items.", {
          variant: "error",
        });
      } finally {
        submitting.current = false;
        setIsSubmitting(false);
      }
    }
  };

  // Memoized so a re-render of this popup cannot rebuild the Form's items.
  // Every item prop below is a fresh object literal and devextreme-react
  // diffs option values by reference, so an unmemoized Form invalidates its
  // layout on every render — remounting the SignatureField and visibly wiping
  // a signature the user already drew (ENG-1793). `users` is the only value
  // this subtree reads, and react-query's structural sharing keeps that
  // reference stable until the data really changes. MUI Modal unmounts its
  // children when closed, so each open still starts from a clean form.
  //
  // openedAt is a dependency so the timestamp editor's default is recomputed
  // each time the modal opens rather than frozen at whenever the user list
  // resolved. It is stable for the whole time the modal is open, so it never
  // triggers the rebuild this memo exists to prevent.
  const formContent = useMemo(
    () => (
      <Form ref={form} colCount={1}>
        <SimpleItem
          dataField="destroyed_by"
          label={{ text: "Destroyed By" }}
          editorType="dxSelectBox"
          isRequired={true}
          editorOptions={{
            dataSource: users,
            searchEnabled: true,
            displayExpr: "full_name",
            dropDownOptions: {
              maxHeight: 250,
            },
            itemTemplate: "destroyed_by",
          }}
        />
        <Template
          name="destroyed_by"
          render={(data) => <UserSelectBoxItem data={data} />}
        />
        <SimpleItem
          dataField="timestamp"
          label={{ text: "Timestamp" }}
          editorType="dxDateBox"
          isRequired={true}
          editorOptions={{
            type: "datetime",
            useMaskBehavior: true,
            displayFormat: getDateFormat({
              isMoment: false,
              includeTime: true,
            }),
            value: openedAt,
          }}
          validationRules={[
            {
              type: "custom",
              message: "Invalid Date",
              validationCallback: validateTimestamp,
            },
          ]}
        />
        <SimpleItem
          dataField="reason"
          label={{ text: "Destroy Notes" }}
          editorType={"dxTextArea"}
        />
        <SimpleItem dataField="custody_from_sig" label={{ visible: false }}>
          <SignatureField
            sigFieldRef={destroyedBySig}
            label="Destroyed By Signature"
            useESignature={true}
          />
        </SimpleItem>
      </Form>
    ),
    [users, openedAt]
  );

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          minHeight: 350,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Destroy Storage Items
        </div>
        <div style={{ marginBottom: 15 }}>
          This will destroy{" "}
          {selectionState === "someIncluded" ? total : `up to ${total}`}{" "}
          selected storage item(s) and record a Destroy chain of custody entry
          for each. Items that are already released or destroyed are skipped.
        </div>
        {users && (
          <>
            {formContent}
            <ToolBarItems
              submitText="Destroy Storage Items"
              onSubmit={handleSubmit}
              onCancel={() => handleClose()}
              loading={isSubmitting}
            />
          </>
        )}
        {!users && <Loader />}
      </div>
    </Modal>
  );
};
